import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";
const SECRET = process.env.REACT_APP_API_SECRET || "";

const headers = {
  "Content-Type": "application/json",
  "X-API-Secret": SECRET,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const stars = (n) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} style={{ color: i < n ? "#f5a623" : "#d0ccc4", fontSize: 14 }}>
      ★
    </span>
  ));

const scoreColor = (s) =>
  s >= 4 ? "#22c55e" : s >= 3 ? "#f5a623" : "#ef4444";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ── Components ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e8e4dc",
      borderRadius: 16,
      padding: "24px 28px",
      borderTop: `3px solid ${accent}`,
    }}>
      <div style={{ fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 1.5, color: "#8c8880", marginBottom: 10 }}>
        {label}
      </div>
      <div style={{ fontSize: 34, fontWeight: 700, color: "#1a1814", fontFamily: "'Instrument Serif', serif" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: "#8c8880", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function RatingBar({ stars: s, count, max }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ fontSize: 13, width: 24, textAlign: "right", color: "#5a5850" }}>{s}★</div>
      <div style={{ flex: 1, height: 8, background: "#f0ede8", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${max ? (count / max) * 100 : 0}%`,
          background: s >= 4 ? "#22c55e" : s >= 3 ? "#f5a623" : "#ef4444",
          borderRadius: 4,
          transition: "width 0.8s ease",
        }} />
      </div>
      <div style={{ fontSize: 12, fontFamily: "monospace", color: "#8c8880", width: 24 }}>{count}</div>
    </div>
  );
}

function SendTestModal({ onClose, onSent }) {
  const [form, setForm] = useState({
    customer_name: "Jane Smith",
    phone: "+15550001234",
    email: "",
    agent_name: "Alex M.",
    call_type: "support",
    call_duration: 180,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!form.customer_name || (!form.phone && !form.email)) {
      setError("Customer name and at least one of phone/email are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/call-ended`, {
        method: "POST",
        headers,
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unknown error");
      onSent(data);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }

  const field = (key, label, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 12, color: "#8c8880", marginBottom: 6, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{
          width: "100%", padding: "10px 14px", background: "#faf9f6", border: "1px solid #e8e4dc",
          borderRadius: 10, color: "#1a1814", fontSize: 14, outline: "none", fontFamily: "inherit",
        }}
      />
    </div>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24,
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: 36, width: "100%", maxWidth: 480,
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, fontWeight: 400 }}>
            Trigger a Test Survey
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#8c8880" }}>✕</button>
        </div>

        {field("customer_name", "Customer Name *", "text", "Jane Smith")}
        {field("phone", "Phone (E.164) *", "tel", "+15550001234")}
        {field("email", "Email (optional)", "email", "jane@example.com")}
        {field("agent_name", "Agent Name", "text", "Alex M.")}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#8c8880", marginBottom: 6, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 1 }}>
              Call Type
            </label>
            <select value={form.call_type} onChange={(e) => setForm({ ...form, call_type: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 10, color: "#1a1814", fontSize: 14, outline: "none" }}>
              <option value="support">Support</option>
              <option value="billing">Billing</option>
              <option value="sales">Sales</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            {field("call_duration", "Duration (sec)", "number", "180")}
          </div>
        </div>

        {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <button onClick={submit} disabled={loading} style={{
          width: "100%", padding: 14, background: "#2b5ce6", color: "#fff", border: "none",
          borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.7 : 1, fontFamily: "inherit",
        }}>
          {loading ? "Sending…" : "▶ Trigger Survey Now"}
        </button>
        <p style={{ fontSize: 12, color: "#8c8880", marginTop: 12, textAlign: "center" }}>
          Survey will be sent after the configured delay (default 3 min)
        </p>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        fetch(`${API}/api/dashboard/stats`, { headers }),
        fetch(`${API}/api/dashboard/responses`, { headers }),
      ]);
      setStats(await sRes.json());
      setResponses(await rRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  function showToast(msg, color = "#22c55e") {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 4000);
  }

  const filtered = filter === "all" ? responses : responses.filter(r => r.score === parseInt(filter));
  const maxDistCount = stats ? Math.max(...stats.distribution.map(d => d.count), 1) : 1;

  const navItem = (id, label, emoji) => (
    <button onClick={() => setTab(id)} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
      background: tab === id ? "#eef2fd" : "transparent",
      color: tab === id ? "#2b5ce6" : "#5a5850",
      border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14,
      fontWeight: tab === id ? 600 : 400, width: "100%", textAlign: "left",
      fontFamily: "inherit",
    }}>
      <span>{emoji}</span> {label}
    </button>
  );

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#faf9f6", fontFamily: "'Outfit', sans-serif", color: "#8c8880" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📞</div>
        <div>Loading dashboard…</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#faf9f6", fontFamily: "'Outfit', sans-serif" }}>

      {/* Sidebar */}
      <aside style={{
        width: 240, background: "#ffffff", borderRight: "1px solid #e8e4dc",
        padding: "32px 16px", display: "flex", flexDirection: "column", position: "fixed", height: "100vh",
      }}>
        <div style={{ paddingLeft: 8, marginBottom: 32 }}>
          <div style={{ fontSize: 22, fontFamily: "'Instrument Serif', serif", fontWeight: 400, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "#eef2fd", borderRadius: 10, padding: "4px 8px", fontSize: 20 }}>📞</span>
            CallRating
          </div>
          <div style={{ fontSize: 12, color: "#8c8880", marginTop: 4, paddingLeft: 4 }}>Post-Call Survey System</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {navItem("overview", "Overview", "📊")}
          {navItem("responses", "Responses", "💬")}
          {navItem("agents", "Agent Stats", "🧑‍💼")}
          {navItem("setup", "Setup Guide", "🔌")}
        </div>

        <div style={{ marginTop: "auto", padding: 12, background: "#f0fdf4", borderRadius: 12, fontSize: 12, color: "#166534" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 2s infinite" }} />
            System Active
          </div>
          <div>Auto-refreshes every 30s</div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ marginLeft: 240, flex: 1, padding: "40px 48px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
          <div>
            <h1 style={{ fontFamily: "'Instrument Serif', serif", fontSize: 32, fontWeight: 400, color: "#1a1814", marginBottom: 4 }}>
              {tab === "overview" ? "Dashboard" : tab === "responses" ? "Responses" : tab === "agents" ? "Agent Performance" : "Setup Guide"}
            </h1>
            <div style={{ fontSize: 14, color: "#8c8880" }}>
              {stats && `${stats.total_rated} ratings · ${stats.response_rate}% response rate`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={load} style={{ padding: "10px 18px", background: "#fff", border: "1px solid #e8e4dc", borderRadius: 10, cursor: "pointer", fontSize: 14, color: "#5a5850", fontFamily: "inherit" }}>
              ↻ Refresh
            </button>
            <button onClick={() => setShowModal(true)} style={{ padding: "10px 20px", background: "#2b5ce6", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" }}>
              ▶ Test Survey
            </button>
          </div>
        </div>

        {/* OVERVIEW TAB */}
        {tab === "overview" && stats && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
              <StatCard label="Surveys Sent" value={stats.total_sent} sub="Since launch" accent="#2b5ce6" />
              <StatCard label="Response Rate" value={`${stats.response_rate}%`} sub={`${stats.total_rated} replied`} accent="#22c55e" />
              <StatCard label="Avg CSAT Score" value={`${stats.avg_score}★`} sub="out of 5.0" accent="#f5a623" />
              <StatCard label="Escalations" value={stats.low_score_alerts} sub="Low-score alerts" accent="#ef4444" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 16, padding: 28 }}>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, marginBottom: 20 }}>Rating Distribution</div>
                {[5,4,3,2,1].map(s => {
                  const d = stats.distribution.find(x => x.stars === s);
                  return <RatingBar key={s} stars={s} count={d?.count || 0} max={maxDistCount} />;
                })}
              </div>

              <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 16, padding: 28 }}>
                <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, marginBottom: 20 }}>Recent Activity</div>
                {responses.slice(0, 5).map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, paddingBottom: 14, marginBottom: 14, borderBottom: i < 4 ? "1px solid #f0ede8" : "none" }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, background: scoreColor(r.score) + "22",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 15, fontWeight: 700, color: scoreColor(r.score), flexShrink: 0,
                    }}>
                      {r.score}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#1a1814" }}>{r.customer_name}</div>
                      <div style={{ fontSize: 12, color: "#8c8880" }}>{r.agent_name} · {fmt(r.created_at)}</div>
                      {r.comment && <div style={{ fontSize: 12, color: "#5a5850", marginTop: 2, fontStyle: "italic" }}>"{r.comment}"</div>}
                    </div>
                  </div>
                ))}
                {responses.length === 0 && <div style={{ fontSize: 14, color: "#8c8880" }}>No responses yet. Send a test survey!</div>}
              </div>
            </div>
          </>
        )}

        {/* RESPONSES TAB */}
        {tab === "responses" && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {["all","5","4","3","2","1"].map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{
                  padding: "6px 14px", borderRadius: 8, border: "1px solid #e8e4dc",
                  background: filter === f ? "#2b5ce6" : "#fff", color: filter === f ? "#fff" : "#5a5850",
                  cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                }}>
                  {f === "all" ? "All" : `${f}★`}
                </button>
              ))}
              <div style={{ marginLeft: "auto", fontSize: 13, color: "#8c8880", alignSelf: "center" }}>
                {filtered.length} responses
              </div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 16, overflow: "hidden" }}>
              {filtered.length === 0 && (
                <div style={{ padding: 48, textAlign: "center", color: "#8c8880", fontSize: 14 }}>No responses yet.</div>
              )}
              {filtered.map((r, i) => (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "36px 1fr auto", gap: 16, alignItems: "start",
                  padding: "16px 24px", borderBottom: i < filtered.length - 1 ? "1px solid #f0ede8" : "none",
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: scoreColor(r.score) + "22",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: scoreColor(r.score),
                  }}>
                    {r.score}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: "#1a1814" }}>{r.customer_name}</span>
                      <span style={{ fontSize: 11, background: r.channel === "sms" ? "#eef2fd" : "#f0fdf4", color: r.channel === "sms" ? "#2b5ce6" : "#166534", padding: "2px 7px", borderRadius: 6, fontFamily: "monospace" }}>
                        {(r.channel || "sms").toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8880", marginBottom: r.comment ? 4 : 0 }}>
                      Agent: {r.agent_name} · {r.call_type} · {fmt(r.created_at)}
                    </div>
                    {r.comment && <div style={{ fontSize: 13, color: "#5a5850", fontStyle: "italic" }}>"{r.comment}"</div>}
                  </div>
                  <div style={{ display: "flex" }}>{stars(r.score)}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* AGENTS TAB */}
        {tab === "agents" && stats && (
          <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0ede8", display: "grid", gridTemplateColumns: "1fr 80px 80px 100px", gap: 16, fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 1, color: "#8c8880" }}>
              <div>Agent</div><div>Responses</div><div>Avg Score</div><div>Rating</div>
            </div>
            {stats.agent_stats.sort((a,b) => b.avg - a.avg).map((a, i) => (
              <div key={i} style={{ padding: "16px 24px", borderBottom: i < stats.agent_stats.length - 1 ? "1px solid #f0ede8" : "none", display: "grid", gridTemplateColumns: "1fr 80px 80px 100px", gap: 16, alignItems: "center" }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: "#1a1814" }}>{a.name}</div>
                <div style={{ fontSize: 14, color: "#8c8880" }}>{a.count}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: scoreColor(a.avg) }}>{a.avg}</div>
                <div>{stars(Math.round(a.avg))}</div>
              </div>
            ))}
            {stats.agent_stats.length === 0 && (
              <div style={{ padding: 48, textAlign: "center", color: "#8c8880", fontSize: 14 }}>No agent data yet.</div>
            )}
          </div>
        )}

        {/* SETUP TAB */}
        {tab === "setup" && (
          <div style={{ maxWidth: 700 }}>
            {[
              {
                step: "1",
                title: "Copy .env.example → .env",
                desc: "Fill in your Twilio and SendGrid credentials.",
                code: `cp backend/.env.example backend/.env\n# Then edit backend/.env with your keys`
              },
              {
                step: "2",
                title: "Install & start the backend",
                code: `cd backend\nnpm install\nnpm start\n# Server runs on http://localhost:3001`
              },
              {
                step: "3",
                title: "Trigger a survey when a call ends",
                desc: "POST this from your phone system or CRM:",
                code: `curl -X POST http://localhost:3001/api/call-ended \\\n  -H "Content-Type: application/json" \\\n  -H "X-API-Secret: YOUR_SECRET" \\\n  -d '{\n    "customer_name": "Jane Smith",\n    "phone": "+15550001234",\n    "email": "jane@example.com",\n    "agent_name": "Alex M.",\n    "call_type": "support",\n    "call_duration": 247\n  }'`
              },
              {
                step: "4",
                title: "Serve the customer survey page",
                desc: "Host customer-survey/index.html on your domain. The backend will link to it automatically.",
                code: `# Set BASE_URL in .env to your public URL:\nBASE_URL=https://surveys.yourcompany.com`
              },
              {
                step: "5",
                title: "Deploy to production",
                desc: "For production: swap the file-based DB for PostgreSQL, run behind nginx, and use PM2 for process management.",
                code: `npm install -g pm2\npm2 start server.js --name callrating\npm2 save && pm2 startup`
              }
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 20, marginBottom: 24 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#eef2fd", color: "#2b5ce6", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {item.step}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#1a1814", marginBottom: 4 }}>{item.title}</div>
                  {item.desc && <div style={{ fontSize: 13, color: "#8c8880", marginBottom: 10 }}>{item.desc}</div>}
                  <pre style={{ background: "#1a1814", color: "#a8e6cf", borderRadius: 10, padding: "14px 18px", fontSize: 12, lineHeight: 1.8, overflow: "auto", fontFamily: "monospace", margin: 0 }}>
                    {item.code}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <SendTestModal
          onClose={() => setShowModal(false)}
          onSent={(data) => {
            setShowModal(false);
            showToast(`✅ Survey queued! Sends in ~${data.message?.match(/\d+/)?.[0] || 3} min`);
            setTimeout(load, 5000);
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 30, right: 30,
          background: toast.color, color: "#fff",
          padding: "12px 20px", borderRadius: 12, fontSize: 14, fontWeight: 500,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)", zIndex: 2000,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
